/**
 * Ask about this (TMMAPP-0151 §B; visual contract v2 §4, §7): the page header's pill. It starts a
 * **private** assistant session about what's on screen — `POST /v1/me/assistant/sessions` with
 * `visibility: PRIVATE`, the page's scope and the registry's default categories — named for the page,
 * and opens it at `/me/assistant/{id}` with the composer **prefilled** with the page's suggested
 * question. It never sends anything: the person sends it, and pays only if they ask.
 *
 * - The scope is what the caller passes (an account, a virtual account, an envelope, or every account
 *   the person owns), kept to what the assistant can scope now (`GET …/options`); when none of it is,
 *   it is sent as passed and the server's refusal reads as a notice.
 * - A GUEST or NONE tier goes through `useSubscriberAction()` (the purchase experience). With AI off
 *   the session still opens, and the assistant page's own AI-off state takes over.
 * - One request however often it's pressed.
 */
import { Box, ButtonBase, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AssistantScopeEntity } from '@/api/assistant';
import { AssistantSparkleNavIcon } from '@/components/icons';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { PILL_RADIUS, resolveTokens } from '@/theme';
import { useAskAssistant } from './useAskAssistant';

export interface AskAboutThisProps {
  /** The session's name ("About Checking", "Your financial picture"). */
  name: string;
  /** What the page shows; `null` while it loads (the pill waits). */
  scope: readonly AssistantScopeEntity[] | null;
}

export function AskAboutThis({ name, scope }: AskAboutThisProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const { ask, busy } = useAskAssistant();

  const action = useSubscriberAction(() => {
    if (scope !== null) void ask({ name, scope, question: t('askAbout.question') });
  });

  return (
    <>
      <ButtonBase
        onClick={action.run}
        disabled={scope === null || busy}
        aria-describedby={action.descriptionId}
        aria-busy={busy || undefined}
        data-ask-about=""
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          p: '9px 14px',
          borderRadius: `${PILL_RADIUS}px`,
          border: '1px solid',
          borderColor: tokens.lineStrong,
          bgcolor: tokens.surface,
          color: tokens.text,
          fontSize: 13,
          lineHeight: '18px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: tokens.brandBoundary },
          '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
          '&.Mui-disabled': { color: tokens.text2 },
        }}
      >
        <Box component="span" aria-hidden sx={{ display: 'flex' }}>
          <AssistantSparkleNavIcon size={16} />
        </Box>
        {t('askAbout.button')}
      </ButtonBase>
      <SubscriberActionHint id={action.descriptionId} description={action.description} />
    </>
  );
}
